/**
 * The states of one generation as the interface sees them.
 *
 * It lives in `types/` because both the hook that computes the phase and the
 * presentation component that draws it need the name, and a component may not
 * import a hook (F-UIX-009). The predicate lives here for the same reason:
 * one definition of "active" for every reader.
 */
export type GenerationPhase =
  | "idle"
  | "starting"
  | "evaluating"
  | "running"
  | "cancelling"
  | "succeeded"
  | "failed"
  | "cancelled"
  | "interrupted"
  | "asked"
  | "briefed";

/**
 * Where a run is in its flow, as its generation stream reports it (F-PRV-003): the role of the
 * step running now (`finishing` once every step is done, `working` for a flow the server could
 * not read, `null` once the run is no longer going), the steps done, how many there are (`null`:
 * unknown) and their roles in order. It names its run, so a late event of another is ignored.
 */
export interface RunProgress {
  readonly generationId: string;
  readonly stage: string | null;
  readonly stepsDone: number;
  readonly stepsTotal: number | null;
  readonly stepRoles: readonly string[];
}

/** A run exists and has not ended: the form is locked and the progress is live. */
export function isActivePhase(phase: GenerationPhase): boolean {
  return (
    phase === "starting" ||
    phase === "evaluating" ||
    phase === "running" ||
    phase === "cancelling"
  );
}
